/**
 * The plan view's states (`spec-onboarding-3d-plan-map-lock`, R3): off → entering → locked →
 * leaving → off, with either move reversible half-way; and `free` in place of `off` when the host asks
 * for it (`spec-onboarding-persistent-scene-lock-poses`, R1): unlocked, with heights following the
 * tilt. Either move can carry a whole camera pose, eased in the same one move (R2, R3).
 *
 * The order of the writes is the point of this hook:
 * - entering: record, lock the controls, ease the pitch to 0, switch the camera to orthographic
 *   only once heights are flat (the switch is then invisible), and pin max pitch 0 only after the
 *   ease (earlier would clamp it);
 * - leaving: free max pitch, restore the projection while heights are still flat, ease back to the
 *   recorded pitch at a zoom inside the recorded limit (or to the leave pose, clamped the same way), and only then restore everything else.
 *
 * Everything that changes per frame lives in a ref; React state changes only on a transition, so
 * the ease never re-renders the tree. While the state is neither `off` nor `free`, this hook is the
 * only writer of the settings in `planMapLock.ts` on its map; in `free` it holds nothing.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  applyPlanLock,
  planEase,
  planHeightScale,
  readPlanLockSettings,
  restorePlanLock,
  PLAN_MAX_ZOOM,
  PLAN_REST_HEIGHT_SCALE,
  PLAN_TILT_MS,
} from './planMapLock';
import type { PlanLockMap, PlanLockSettings, PlanPose, PlanViewState } from './planMapLock';

type CameraTarget = {
  pitch: number;
  zoom?: number;
  center?: { lng: number; lat: number };
  bearing?: number;
};

/** The gestures the lock leaves on while locked (unless asked not to); held off during the tilt. */
const GESTURES = ['dragPan', 'scrollZoom', 'touchZoomRotate'] as const;
type GestureName = (typeof GESTURES)[number];

/** What the hook needs beyond the lock: the camera moves and events. `mapboxgl.Map` satisfies it. */
export interface PlanCameraMap extends PlanLockMap {
  getZoom(): number;
  easeTo(
    options: CameraTarget & { duration: number; easing: (k: number) => number },
    eventData?: Record<string, unknown>,
  ): unknown;
  jumpTo(options: CameraTarget, eventData?: Record<string, unknown>): unknown;
  stop(): unknown;
  /** Asks for a frame: a `free`/`off` swap changes the heights with no camera move. */
  triggerRepaint(): void;
  on(type: string, listener: (e?: any) => void): unknown;
  off(type: string, listener: (e?: any) => void): unknown;
}

export interface PlanMapLockOptions {
  planView: boolean;
  /** False jumps instead of easing. The user's reduced-motion setting does the same. */
  animate?: boolean;
  /**
   * False holds Mapbox's pan, scroll zoom and touch zoom off while locked (`applyPlanLock`); leaving
   * puts back what was recorded. Defaults to true.
   */
  mapGestures?: boolean;
  /**
   * With `planView` false, `free` instead of `off`: heights follow the tilt (R1). Ignored while
   * `planView` is true. Defaults to false.
   */
  free?: boolean;
  /** Where a leave ends (R2); absent, the recorded pitch at a zoom inside the recorded limit. */
  leavePose?: PlanPose | null;
  /** Where an enter ends, flat (R3); absent, the camera stays where it is and only flattens. */
  enterPose?: PlanPose | null;
}

export interface PlanMapLock {
  state: PlanViewState;
  /** True once the hook has resolved its first state on a loaded map. */
  settled: boolean;
  /** The current height scale. Cheap; read it every frame. */
  getHeightScale: () => number;
  /** The current state, read from the ref (no render needed). */
  getState: () => PlanViewState;
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const finite = (...v: number[]) => v.every(Number.isFinite);

/**
 * A pose the hook can ease to, or `null` (spec, Inputs): a non-finite value makes the whole pose
 * absent for that move (Mapbox throws on a non-finite centre), and `onRejected` is told. A pitch is
 * checked wherever it is given; entering ignores it.
 */
const readPose = (pose: PlanPose | null | undefined, onRejected: () => void): PlanPose | null => {
  if (!pose) return null;
  const ok =
    finite(pose.center?.lng, pose.center?.lat, pose.zoom, pose.bearing) &&
    (pose.pitch === undefined || Number.isFinite(pose.pitch));
  if (ok) return pose;
  onRejected();
  return null;
};

interface Machine {
  phase: PlanViewState;
  recorded: PlanLockSettings | null;
  startPitch: number;
  startScale: number;
  targetPitch: number;
  /** The current move's whole target: where an ease stopped part-way is finished (R4). */
  target: CameraTarget;
  /** The current leave ends in `free` (R1, R2). */
  toFree: boolean;
  progress: number;
  orthoApplied: boolean;
  /**
   * The three gestures as the lock left them, read right after `applyPlanLock` on an enter from
   * `off`: what the end of the tilt hands back. `applyPlanLock` alone decides them.
   */
  lockedGestures: Record<GestureName, boolean> | null;
  /** Identifies this hook's current ease; any other `moveend` is ignored. */
  token: number;
}

export const usePlanMapLock = (
  map: PlanCameraMap | null,
  {
    planView,
    animate = true,
    mapGestures = true,
    free = false,
    leavePose = null,
    enterPose = null,
  }: PlanMapLockOptions,
): PlanMapLock => {
  const machine = useRef<Machine>({
    phase: 'off',
    recorded: null,
    startPitch: 0,
    startScale: 1,
    targetPitch: 0,
    target: { pitch: 0 },
    toFree: false,
    progress: 0,
    orthoApplied: false,
    lockedGestures: null,
    token: 0,
  });
  const mapRef = useRef(map);
  mapRef.current = map;
  const planViewRef = useRef(planView);
  planViewRef.current = planView;
  const animateRef = useRef(animate);
  animateRef.current = animate;
  const mapGesturesRef = useRef(mapGestures);
  mapGesturesRef.current = mapGestures;
  // Read at the moment the hook acts, so a pose arriving with its request is never stale.
  const freeRef = useRef(free);
  freeRef.current = free;
  const leavePoseRef = useRef(leavePose);
  leavePoseRef.current = leavePose;
  const enterPoseRef = useRef(enterPose);
  enterPoseRef.current = enterPose;
  // Once per hook instance (plan, Checkpoint 2).
  const warnedPoseRef = useRef(false);
  const rejectPose = useCallback(() => {
    if (warnedPoseRef.current) return;
    warnedPoseRef.current = true;
    console.warn('[usePlanMapLock] a non-finite pose was ignored');
  }, []);
  const driveRef = useRef<(() => void) | null>(null);

  const [state, setState] = useState<PlanViewState>('off');
  const [settled, setSettled] = useState(false);

  const getHeightScale = useCallback(() => {
    const m = machine.current;
    const live = mapRef.current;
    return planHeightScale({
      state: m.phase,
      pitch: live ? live.getPitch() : 0,
      startPitch: m.startPitch,
      startScale: m.startScale,
      targetPitch: m.targetPitch,
      progress: m.progress,
      toFree: m.toFree,
    });
  }, []);
  const getState = useCallback(() => machine.current.phase, []);

  useEffect(() => {
    if (!map) return undefined;
    const m = machine.current;
    const setPhase = (p: PlanViewState) => {
      m.phase = p;
      setState(p);
    };
    const instant = () => !animateRef.current || prefersReducedMotion();
    const easing = (token: number) => (k: number) => {
      const e = planEase(k);
      if (m.token === token) m.progress = e;
      return e;
    };
    const applyOrtho = () => {
      map.setCamera({ 'camera-projection': 'orthographic' });
      m.orthoApplied = true;
    };

    // Pan and zoom gestures stop a Mapbox ease part-way, so they are held off for the second the
    // tilt takes and handed back the moment it ends.
    const holdGestures = () => GESTURES.forEach(h => map[h].disable());
    const releaseGestures = (locked: Record<GestureName, boolean>) =>
      GESTURES.forEach(h => (locked[h] ? map[h].enable() : map[h].disable()));

    const finishEnter = () => {
      m.progress = 1;
      if (!m.orthoApplied) applyOrtho();
      map.setMaxPitch(0);
      if (m.lockedGestures) releaseGestures(m.lockedGestures);
      setPhase('locked');
    };
    const finishLeave = () => {
      if (m.recorded) restorePlanLock(map, m.recorded);
      m.recorded = null;
      m.progress = 1;
      // The host's latest ask, which `drive` has also given the heights (R1.3).
      setPhase(m.toFree ? 'free' : 'off');
    };

    const enter = (forceInstant = false) => {
      const scaleNow = getHeightScale();
      // A new token first: stopping an ease fires its `moveend`, which must read as stale.
      const token = ++m.token;
      if (m.phase === 'leaving') map.stop();
      // From `free` as from `off`: the leave that reached it put everything back.
      const fromOff = m.phase === 'off' || m.phase === 'free';
      if (fromOff) m.recorded = readPlanLockSettings(map);
      applyPlanLock(map, { mapGestures: mapGesturesRef.current });
      // Read before the hold, and only from `off`: mid-leave the gestures are still held, and the
      // reading taken when this lock began still stands.
      if (fromOff) {
        const locked = {} as Record<GestureName, boolean>;
        GESTURES.forEach(h => {
          locked[h] = map[h].isEnabled();
        });
        m.lockedGestures = locked;
      }
      holdGestures();
      m.startPitch = map.getPitch();
      m.startScale = scaleNow;
      m.progress = 0;
      m.orthoApplied = false;
      m.toFree = false;
      // R3: a pose moves with the pitch in the one ease; the forced mount enter takes none, as the
      // camera has just been placed. `applyPlanLock` has already raised max zoom to the plan's.
      const pose = forceInstant ? null : readPose(enterPoseRef.current, rejectPose);
      m.target = pose
        ? {
            center: { lng: pose.center.lng, lat: pose.center.lat },
            zoom: Math.min(pose.zoom, PLAN_MAX_ZOOM),
            bearing: pose.bearing,
            pitch: 0,
          }
        : { pitch: 0 };
      setPhase('entering');
      if (forceInstant || instant()) {
        map.jumpTo(m.target);
        finishEnter();
      } else {
        map.easeTo(
          { ...m.target, duration: PLAN_TILT_MS, easing: easing(token) },
          { planMapLock: token },
        );
      }
    };

    const leave = () => {
      const recorded = m.recorded;
      if (!recorded) return;
      const scaleNow = getHeightScale();
      const token = ++m.token;
      if (m.phase === 'entering') map.stop();
      holdGestures();
      map.setMaxPitch(recorded.maxPitch);
      // Back to the recorded projection while the plan is still flat.
      map.setCamera({ 'camera-projection': recorded.projection });
      m.orthoApplied = false;
      m.startPitch = map.getPitch();
      m.startScale = scaleNow;
      m.toFree = freeRef.current;
      // R2: a pose moves centre, zoom, bearing and pitch in the one ease, inside the limits that
      // hold once it ends. Without the zoom term, restoring a max zoom of 22 after a zoom-24 plan
      // would snap.
      const pose = readPose(leavePoseRef.current, rejectPose);
      m.target = pose
        ? {
            center: { lng: pose.center.lng, lat: pose.center.lat },
            zoom: Math.min(Math.max(pose.zoom, recorded.minZoom), recorded.maxZoom),
            bearing: pose.bearing,
            // A pose given without a pitch keeps the recorded one; either is held to the recorded
            // range, so restoring the limits after the ease never snaps the camera.
            pitch: Math.min(
              Math.max(pose.pitch ?? recorded.pitch, recorded.minPitch),
              recorded.maxPitch,
            ),
          }
        : { pitch: recorded.pitch, zoom: Math.min(map.getZoom(), recorded.maxZoom) };
      m.targetPitch = m.target.pitch;
      m.progress = 0;
      setPhase('leaving');
      if (instant()) {
        map.jumpTo(m.target);
        finishLeave();
      } else {
        map.easeTo(
          { ...m.target, duration: PLAN_TILT_MS, easing: easing(token) },
          { planMapLock: token },
        );
      }
    };

    const onMove = () => {
      if (m.phase === 'entering' && !m.orthoApplied && getHeightScale() <= PLAN_REST_HEIGHT_SCALE) {
        applyOrtho();
      }
    };
    const onMoveEnd = (e?: { planMapLock?: number }) => {
      if (!e || e.planMapLock !== m.token) return;
      // Our own ease, stopped before its last frame (by anything other than this hook, which
      // retires the token first): finish at the whole target rather than wherever it stopped (R4).
      // A caller's own `jumpTo` stops first in Mapbox, so its camera is still the one that stays.
      const interrupted = m.progress < 1;
      if (m.phase === 'entering') {
        if (interrupted) map.jumpTo(m.target);
        finishEnter();
      } else if (m.phase === 'leaving') {
        if (interrupted && m.recorded) map.jumpTo(m.target);
        finishLeave();
      }
    };
    // A style reload drops the camera projection; limits and handlers are not part of the style.
    const onStyleLoad = () => {
      if (m.phase === 'locked' || (m.phase === 'entering' && m.orthoApplied)) {
        map.setCamera({ 'camera-projection': 'orthographic' });
      }
    };

    map.on('move', onMove);
    map.on('moveend', onMoveEnd);
    map.on('style.load', onStyleLoad);

    // Drive transitions from the latest props; read through refs so this effect runs once per map.
    const drive = () => {
      const unlocked = m.phase === 'off' || m.phase === 'free';
      if (planViewRef.current && (unlocked || m.phase === 'leaving')) enter();
      else if (!planViewRef.current && (m.phase === 'locked' || m.phase === 'entering')) leave();
      // R1.3: `free` and `off` swap on the option alone, with no camera move. A leave in flight
      // takes the new end at once, so its heights and the state it lands in agree.
      else if (!planViewRef.current && m.phase === 'leaving') m.toFree = freeRef.current;
      else if (!planViewRef.current && unlocked) {
        const next = freeRef.current ? 'free' : 'off';
        if (next !== m.phase) {
          setPhase(next);
          map.triggerRepaint();
        }
      }
    };
    driveRef.current = drive;

    // Mounting with the plan view on locks at once, without animation.
    if (planViewRef.current) enter(true);
    else setPhase(freeRef.current ? 'free' : 'off');
    setSettled(true);

    return () => {
      map.off('move', onMove);
      map.off('moveend', onMoveEnd);
      map.off('style.load', onStyleLoad);
      driveRef.current = null;
      // `free` holds nothing to put back, and a stop would end the person's own ease.
      if (m.phase !== 'off' && m.phase !== 'free') {
        m.token += 1;
        try {
          map.stop();
          if (m.recorded) {
            restorePlanLock(map, m.recorded);
            // The camera's tilt is recorded too; a map that outlives the hook gets it back.
            map.jumpTo({
              pitch: m.recorded.pitch,
              zoom: Math.min(map.getZoom(), m.recorded.maxZoom),
            });
          }
        } catch {
          // The map was already removed; there is nothing left to restore.
        }
      }
      m.phase = 'off';
      m.recorded = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // `driveRef` is set only once the first state has resolved on a loaded map.
  useEffect(() => {
    driveRef.current?.();
  }, [planView, free]);

  // A change of `mapGestures` while the lock holds (the onboarding stage from a washed step into
  // Position on map stays locked): the three gestures follow it as an enter would have set them, on
  // from what was recorded, off otherwise. Mid-tilt they stay held, and the tilt's end hands back the
  // new set. The camera does not move, and leaving still restores the recorded settings.
  const lastGesturesRef = useRef(mapGestures);
  useEffect(() => {
    if (lastGesturesRef.current === mapGestures) return;
    lastGesturesRef.current = mapGestures;
    const live = mapRef.current;
    const m = machine.current;
    if (!live || !m.recorded || (m.phase !== 'locked' && m.phase !== 'entering')) return;
    const next = {} as Record<GestureName, boolean>;
    GESTURES.forEach(h => {
      next[h] = mapGestures && m.recorded!.handlers[h];
    });
    m.lockedGestures = next;
    if (m.phase === 'locked') {
      GESTURES.forEach(h => (next[h] ? live[h].enable() : live[h].disable()));
    }
  }, [mapGestures]);

  return { state, settled, getHeightScale, getState };
};
